import { useEffect, useRef } from 'react'

/** Ein Feld, in dem getippt wird — dort ist Q ein Buchstabe. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return true
  // `isContentEditable` reicht im Browser; der Blick nach oben fängt auch Editoren, die es nicht melden.
  return target.isContentEditable || target.closest('[contenteditable]:not([contenteditable="false"])') != null
}

/**
 * Was gerade seine eigenen Tasten hat: ein offener Dialog, ein Menü, ein Kasten der Titelzeile.
 * Die ausgeblendete Schnelleingabe im Web bleibt eingehängt — `data-state="closed"` zählt nicht.
 */
const OPEN_LAYER = '[role="dialog"]:not([data-state="closed"]), [role="alertdialog"], [role="menu"], [data-task-dropdown]'

/** Ein Dialog, Menü oder Kasten ist offen — seine Tasten gehören ihm (auch dem Einsortieren, Diff-Audit 06.10.2026). */
export function openLayerPresent(): boolean {
  return document.querySelector(OPEN_LAYER) != null
}

/**
 * **`Q` öffnet die Schnelleingabe** (#234, F5) — überall in der App, solange nicht getippt wird und
 * kein Dialog, Menü oder Kasten offen ist. Ohne Umschalt und ohne ⌘/Strg/⌥: Die gehören anderen.
 *
 * In der Blasenphase: Wer die Taste schon für sich genommen hat (`preventDefault`), behält sie.
 */
export function useQuickAddKey(onOpen: () => void, enabled = true): void {
  const openRef = useRef(onOpen)
  openRef.current = onOpen

  useEffect(() => {
    if (!enabled) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.repeat || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
      if (e.key.toLowerCase() !== 'q') return
      if (isTypingTarget(e.target) || openLayerPresent()) return
      e.preventDefault()
      openRef.current()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [enabled])
}
